import { RelativePosition } from "@/api/dtos";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

/** Aligné sur le bouton « Retour à la liste » du FormHeader. */
export const sheetNavButtonClass =
  "inline-flex h-9 items-center gap-1 rounded-lg border border-input bg-white px-3 py-2 text-sm font-semibold font-['Raleway'] text-[#005d8d] shadow-sm hover:bg-slate-100 whitespace-nowrap";

export interface RelativePositionNavProps {
  relativePosition: RelativePosition | undefined;
  buildLink: (id: string) => string;
  resourceType: string;
  /** Optional center content (e.g. title) rendered alongside position/total */
  children?: ReactNode;
  /**
   * `around` (défaut) : précédent | titre | suivant (prévisualisation).
   * `adjacent` : précédent + suivant côte à côte (barre fiche, à droite).
   */
  layout?: "around" | "adjacent";
  className?: string;
}

export function RelativePositionNav({
  relativePosition,
  buildLink,
  resourceType,
  children,
  layout = "around",
  className,
}: RelativePositionNavProps) {
  const { t } = useTranslation();

  const previousLink = relativePosition?.previousId ? (
    <Link
      className={sheetNavButtonClass}
      to={buildLink(relativePosition.previousId)}
    >
      <ChevronLeftIcon className="h-3.5 w-3.5 shrink-0 text-[#005d8d]" />
      <span>{t(`previous_${resourceType}`)}</span>
    </Link>
  ) : null;

  const nextLink = relativePosition?.nextId ? (
    <Link
      to={buildLink(relativePosition.nextId)}
      className={sheetNavButtonClass}
    >
      <span>{t(`next_${resourceType}`)}</span>
      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-[#005d8d]" />
    </Link>
  ) : null;

  if (layout === "adjacent") {
    if (!previousLink && !nextLink) return null;
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {previousLink}
        {nextLink}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid w-full grid-cols-[auto_1fr_auto] place-items-center gap-7",
        className,
      )}
    >
      <div>{previousLink}</div>
      <div className="flex max-w-full min-w-4 items-baseline justify-center gap-3">
        {children}
        {relativePosition && (
          <span className="w-fit text-xs font-semibold text-[hsla(222,41%,8%,0.5)]">
            {relativePosition.position}/{relativePosition.total}
          </span>
        )}
      </div>
      <div>{nextLink}</div>
    </div>
  );
}
